import * as PropTypes from 'prop-types';
import * as React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { BellIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import Badge, { KINDS, SIZES } from '../Badge';
import Button from '../Button';
import AppStoreBadge from '../../assets/app_store_badge.png';
import GooglePlayBadge from '../../assets/google_play_badge.png';
import AppsImage from '../../assets/apps.png';

const onClickDesktopLandingPage = () => window.open('https://www.medflex.de/desktop-app', '_blank');

const News = ({ onShareWithContacts }) => {
  const { t } = useTranslation('components', { keyPrefix: 'physician_welcome_screen.news' });

  return (
    <div data-component="PhysicianWelcomeScreen__News" className="mb-8">
      <h2 className="font-semibold text-xl text-slate-500 mb-2 flex-1">
        <Trans t={t} i18nKey="title" />
      </h2>
      <div className="w-full flex flex-col lg:flex-row lg:space-x-4 border-slate-200 border bg-white p-4 mb-5">
        <video
          preload="none"
          className="flex-grow lg:max-w-half mb-4 lg:mb-0 w-full"
          controls
          poster="../../assets/pam-preview.png"
        >
          <track kind="captions" />
          <source
            src="https://assets.shared.medflex.de/media/medflex_ihre_patientenkommunikation_an_einem_ort.webm"
            type="video/webm"
          />

          {t('pam.not_supported')}
        </video>
        <div className="lg:flex-grow" />
        <div className="max-w-md self-center">
          <a
            href="https://landing.medflex.de/telefonassistent-broschuere-monate"
            target="_blank"
            rel="noreferrer"
          >
            <Badge className="mb-5 bg-teal-100 p-1" kind={KINDS.SIGNAL} size={SIZES.SMALL}>
              <InformationCircleIcon className="text-teal-500 w-4" />
              <span className="text-teal-800 text-sm font-semibold whitespace-nowrap ml-1">
                {t('pam.badge')}
              </span>
              <span className="whitespace-nowrap text-sm underline text-teal-800 ml-1 font-normal">
                {t('pam.badge_link')}
              </span>
            </Badge>
          </a>

          <p className="text-blue-700 mb-4 text-xl font-semibold">
            <Trans t={t} i18nKey="pam.title" />
          </p>
          <p className="mb-4">
            <Trans t={t} i18nKey="pam.description" />
          </p>
          <a
            className="text-blue-500 font-semibold"
            href="https://landing.medflex.de/telefonassistent-broschuere-monate"
            target="_blank"
            rel="noreferrer"
          >
            {t('pam.link')}
          </a>
        </div>
      </div>

      <div className="w-full flex flex-col lg:flex-row lg:space-x-4 border-slate-200 border bg-white p-4 mb-5">
        <div className="flex-grow lg:max-w-half mb-4 lg:mb-0 w-full">
          <img alt={t('apps.apps_image_badge_alt_text')} src={AppsImage} className="w-full" />
        </div>
        <div className="lg:flex-grow" />
        <div className="max-w-md self-center">
          <span className="text-blue-700 text-xl font-semibold mb-4">{t('apps.title')}</span>
          <p className="mb-6">{t('apps.description')}</p>
          <Button kind="link" className="text-blue-500 mb-4" onClick={onShareWithContacts}>
            {t('apps.share_with_contact')}
          </Button>
          <Button kind="primary" className="mb-4" onClick={onClickDesktopLandingPage}>
            {t('apps.desktop_cta_text')}
          </Button>
          <span className="block text-blue-700 mb-4">{t('apps.mobile_apps_text')}</span>
          <div className="flex">
            <a
              href="https://apps.apple.com/de/app/medflex/id1604366389"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img className="h-10" alt={t('apps.app_store_badge_alt_text')} src={AppStoreBadge} />
            </a>
            <a
              className="ml-4"
              href="https://play.google.com/store/apps/details?id=de.medflex.app.flutter"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                className="h-10"
                alt={t('apps.google_play_badge_alt_text')}
                src={GooglePlayBadge}
              />
            </a>
          </div>
        </div>
      </div>

      <div className="w-full flex border-slate-200 border bg-slate-100 p-4 mb-5 justify-center">
        <a
          href="https://medflex.de/hilfe-category/behandler-neue-funktionen/"
          target="_blank"
          rel="noreferrer"
          className="news-element"
        >
          <div className="text-blue-500 flex items-center space-x-1.5">
            <BellIcon className="h-5 w-5 fill-current" />
            <span>{t('newFeatures')}</span>
          </div>
        </a>
      </div>
    </div>
  );
};

News.defaultProps = {
  onShareWithContacts: () => {},
};

News.propTypes = {
  onShareWithContacts: PropTypes.func,
};

export default News;
